import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import PillarLayout from './PillarLayout';

const CANONICAL = 'https://enbet-consulting.de/ki-fuer-filialisten';

const faqItems = [
  {
    q: 'Was unterscheidet KI für Filialisten von KI für Einzelläden?',
    a: 'Filialisten haben drei zusätzliche Herausforderungen: Konsistenz über Standorte hinweg, lokale Identität trotz zentraler Vorgaben, und Datenflüsse zwischen Filialen und Zentrale. KI muss hier nicht nur einzelne Prozesse optimieren, sondern als gemeinsame Schicht über alle Standorte funktionieren. Das heißt: einheitliche Datenmodelle, klare Verantwortungen zwischen Zentrale und Filiale, und Lösungen die auch der Filialleiter ohne IT-Hintergrund bedienen kann.',
  },
  {
    q: 'Welche KI-Use-Cases haben für Filialnetze den größten Hebel?',
    a: 'Vier Use-Cases skalieren überproportional, wenn man sie über mehrere Standorte ausrollt. (1) Personaleinsatzplanung auf Basis von Frequenzdaten pro Filiale — bessere Schichten, weniger Leerlauf, weniger Überstunden. (2) Bestandsoptimierung mit lokaler Nachfrageprognose — weniger Out-of-Stock, weniger Abschriften. (3) KI-gestützte Mitarbeiter-Assistenz für Service-Standards und Produktwissen — gleiche Qualität in 10 oder 100 Filialen. (4) Zentrales Reporting mit automatisierter Auswertung von Kundenfeedback aus allen Standorten.',
  },
  {
    q: 'Wie verhindert man, dass KI-Rollouts in Filialnetzen scheitern?',
    a: 'Die häufigste Fehlerquelle: ein Use-Case wird in einer Pilot-Filiale gebaut und dann „ausgerollt“ – ohne dass die anderen Filialen die Vorbedingungen erfüllen (Datenqualität, WLAN, Schulungsstand). Die saubere Reihenfolge: erst Standort-Check (Modul 1) als Querschnitt über repräsentative Filialen, dann Strategie-Workshop (Modul 2) mit der Zentrale und 2–3 Filialleitern, dann ein bewusster Pilot in einer Filiale, dann strukturierter Rollout mit klaren Rollen.',
  },
  {
    q: 'Wie binde ich Franchise-Nehmer in eine KI-Strategie ein?',
    a: 'Franchise-Systeme haben ein zusätzliches Spannungsfeld: zentrale Marke und Standards versus lokale unternehmerische Freiheit. KI funktioniert hier nur als Angebot, nicht als Vorgabe. Konkret: zentrale Tools bereitstellen, klare Mehrwerte für Franchise-Nehmer kommunizieren (Zeitersparnis, höherer Umsatz pro Mitarbeiter), pragmatische Schulung anbieten, Datennutzung DSGVO-konform regeln (R im ROAD-Framework). Wer KI im Franchise-System mit der Brechstange einführt, verliert Akzeptanz.',
  },
  {
    q: 'Ab welcher Filialgröße lohnt sich KI?',
    a: 'Pragmatisch: ab 3–5 Filialen, weil dann manuelle Steuerung an Grenzen stößt und der Hebel über mehrere Standorte skaliert. Aber: nicht die Anzahl entscheidet, sondern die Datenlage und die Bereitschaft zur Standardisierung. Ein Filialist mit 4 Standorten und sauberer Warenwirtschaft profitiert mehr als einer mit 50 Standorten und 50 Excel-Insellösungen.',
  },
];

const longTailFaq = [
  {
    q: 'Wie behandelt KI lokale Sortimentsunterschiede zwischen Filialen?',
    a: 'Moderne Bedarfsprognose-Modelle nutzen lokale Frequenz- und Verkaufsdaten als Features. Eine Filiale in der Innenstadt bekommt andere Sortimentempfehlungen als eine im Gewerbegebiet. Voraussetzung: die Filialdaten müssen sauber getaggt sein und mindestens 6–12 Monate Historie haben.',
  },
  {
    q: 'Was kostet ein KI-Rollout über 10–50 Filialen?',
    a: 'Das Pilot-Setup für eine Filiale liegt typischerweise bei 15.000–40.000 € (netto). Der Rollout auf 10–50 Standorte skaliert nicht linear — pro zusätzlicher Filiale fallen oft nur 5–10 % der Pilotkosten an, wenn die Architektur sauber geplant wurde. Eine fundierte Strategie (Modul 2, 3.500 € netto Festpreis) mit ROI-Kalkulation ist der Hebel, um Pilotkosten realistisch zu planen statt im Nachhinein zu rechtfertigen. Alle Preise verstehen sich zzgl. 19 % MwSt.',
  },
  {
    q: 'Wie verhindere ich, dass mein Filialleiter sich von KI bevormundet fühlt?',
    a: 'KI als Vorschlag, nicht als Vorschrift. Schichtpläne werden vorgeschlagen, der Filialleiter entscheidet final. Empfehlungen für Sortimentsschwerpunkte sind Vorschläge, die der Filialleiter mit lokalem Wissen überschreiben darf. Das ist nicht weniger effektiv – im Gegenteil: Filialleiter, die sich gesehen fühlen, werden zu KI-Botschaftern. Schulungsangebote, nicht Schulungspflicht.',
  },
];

export default function PillarFuerFilialisten() {
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: 'KI für Filialisten – wie KI über mehrere Standorte hinweg wirklich funktioniert',
        description: 'Multi-Standort-Strategien für KI in Filialnetzen: Konsistenz, lokale Identität, Rollout ohne Akzeptanzverlust.',
        author: { '@id': 'https://enbet-consulting.de/#sebastian-funke' },
        publisher: { '@id': 'https://enbet-consulting.de/#organization' },
        mainEntityOfPage: CANONICAL,
        inLanguage: 'de-DE',
        about: 'KI für Filialisten',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI im stationären Handel', item: 'https://enbet-consulting.de/ki-im-stationaeren-handel' },
          { '@type': 'ListItem', position: 3, name: 'KI für Filialisten', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [...faqItems, ...longTailFaq].map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI für Filialisten – wie Multi-Standort-KI wirklich funktioniert | ENBET Consulting"
      description="KI für Filialisten und Franchise-Systeme: Konsistenz über Standorte, lokale Identität, Rollout ohne Akzeptanzverlust. Fünf Use-Cases mit messbarem Hebel."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            Multi-Location · Filialnetze & Franchise
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.15] mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            KI für Filialisten – wie Multi-Standort-KI wirklich funktioniert
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Filialnetze und Franchise-Systeme stehen vor einem doppelten Problem: Konsistenz über Standorte – und gleichzeitig lokale Identität. Diese Seite zeigt, wie KI beides hinbekommt, ohne dass Filialleiter sich übergangen fühlen.
          </motion.p>
        </div>
      </section>

      {/* Kurzantwort */}
      <section className="bg-[#f7f7f7] py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kurzantwort
          </p>
          <p className="text-[#1f1f1f] text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            KI in Filialnetzen wirkt überproportional, wenn sie als gemeinsame Schicht über alle Standorte gedacht ist – nicht als Inselprojekt einer Pilot-Filiale. Der Schlüssel: ehrlicher Standort-Check über repräsentative Filialen, einheitliche Datenmodelle, klare Verantwortungen zwischen Zentrale und Filiale, und Tools die auch ohne IT-Hintergrund bedienbar sind. Wer das umdreht, scheitert beim Rollout.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-16">
          {faqItems.map((item, i) => (
            <article key={i} className="scroll-mt-24" id={`q-${i + 1}`}>
              <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.q}
              </h2>
              <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {item.a}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Mini-FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Folgefragen
          </h2>
          <div className="space-y-8">
            {longTailFaq.map((item, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.q}
                </h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verwandte Pillars */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Weiter vertiefen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel', sub: 'Grundlagen, Use-Cases und Kosten – der Pillar-Artikel.' },
              { to: '/ki-strategie-retail', label: 'KI-Strategie Retail', sub: 'Konkrete Roadmap und ROI-Kalkulation aus dem Workshop.' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel', sub: 'Lokale Beratung aus Nordhessen für ganz Deutschland.' },
            ].map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className="border border-[#e5e5e5] p-6 hover:border-[#befa0a] transition-colors flex flex-col gap-3"
              >
                <ArrowRight size={18} className="text-[#befa0a]" />
                <span className="text-[#1f1f1f] text-base font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {link.label}
                </span>
                <span className="text-[#1f1f1f]/60 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {link.sub}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Filialnetz analysieren: kostenloser 20-Minuten Potenzial-Check
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wir schauen uns dein Filialnetz an und sagen, wo KI sich für dich rentiert – ohne Verkaufsdruck, ohne Folien.
          </p>
          <Link
            to="/#booking"
            className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            <Check size={16} /> Termin vereinbaren
          </Link>
        </div>
      </section>
    </PillarLayout>
  );
}
